<template>
  <div class="app-container sorting-metric-report">
    <el-card shadow="never" class="page-card page-intro">
      <div class="intro-main">
        <div class="intro-heading">
          <div class="intro-title-row">
            <div class="page-title">{{ $t('CenterForSorting.sortingMetricReport') }}</div>
            <div class="page-desc">{{ $t('CenterForSorting.sortingMetricReportDesc') }}</div>
          </div>
        </div>
      </div>
    </el-card>

    <div class="intro-meta" :class="{ 'intro-meta--compact': isIntroMetaCompact }">
      <div class="meta-item">
        <div class="meta-label">{{ $t('CenterForSorting.machine') }}</div>
        <el-tooltip :content="currentMachineLabel" effect="dark" placement="top">
          <div class="meta-value">{{ currentMachineLabel }}</div>
        </el-tooltip>
      </div>
      <div class="meta-item">
        <div class="meta-label">{{ $t('CenterForSorting.sortingTimeDimension') }}</div>
        <el-tooltip :content="currentQueryTypeLabel" effect="dark" placement="top">
          <div class="meta-value">{{ currentQueryTypeLabel }}</div>
        </el-tooltip>
      </div>
      <div class="meta-item">
        <div class="meta-label">{{ $t('CenterForSorting.sortingStatisticType') }}</div>
        <el-tooltip :content="currentMetricLabel" effect="dark" placement="top">
          <div class="meta-value">{{ currentMetricLabel }}</div>
        </el-tooltip>
      </div>
      <div class="meta-item meta-item--range">
        <div class="meta-label">{{ $t('CenterForSorting.sortingReportTimeRange') }}</div>
        <div class="meta-range-row">
          <el-tooltip :content="reportTimeRangeText" effect="dark" placement="top">
            <div class="meta-value meta-value--small">{{ reportTimeRangeText }}</div>
          </el-tooltip>
          <el-button
            icon="el-icon-refresh"
            size="mini"
            class="meta-refresh-btn"
            :loading="isFetchingDashboard"
            @click="handleManualRefresh"
          >
            {{ $t('CenterForSorting.refresh') }}
          </el-button>
        </div>
      </div>
    </div>

    <el-card v-loading="pageLoading" shadow="never" class="page-card filter-card">
      <div class="metric-tab-bar">
        <el-tabs v-model="activeMetricType" class="metric-tabs" @tab-click="handleMetricTabClick">
          <el-tab-pane :label="$t('CenterForSorting.sortingChuteStat')" name="1" />
          <el-tab-pane :label="$t('CenterForSorting.sortingSupplyTableStat')" name="2" />
          <el-tab-pane :label="$t('CenterForSorting.sortingRuleHitStat')" name="3" />
        </el-tabs>
        <el-tooltip :content="currentMetricDesc" effect="dark" placement="top">
          <div class="metric-desc-trigger">
            <i class="el-icon-info" />
            <span>{{ $t('CenterForSorting.sortingMetricDescLabel') }}</span>
          </div>
        </el-tooltip>
      </div>

      <el-form
        :inline="true"
        :model="queryForm"
        label-position="top"
        size="small"
        class="filter-form"
      >
        <el-form-item :label="$t('CenterForSorting.machine')">
          <el-select
            v-model="queryForm.machineCode"
            :placeholder="$t('CenterForSorting.pleaseChoose')"
            clearable
            filterable
            @change="handleMachineChange"
          >
            <el-option
              v-for="item in machineList"
              :key="item.machineCode"
              :label="item.machineName + '(' + item.machineCode + ')'"
              :value="item.machineCode"
            />
          </el-select>
        </el-form-item>

        <el-form-item :label="$t('CenterForSorting.sortingTimeDimension')">
          <el-select
            v-model="queryForm.queryType"
            :placeholder="$t('CenterForSorting.pleaseChoose')"
            @change="handleQueryTypeChange"
          >
            <el-option
              v-for="item in queryTypeOptions"
              :key="item.value"
              :label="$t(item.labelKey)"
              :value="item.value"
            />
          </el-select>
        </el-form-item>

        <el-form-item
          v-if="showSupplyTableFilter"
          :label="$t('CenterForSorting.supplyTable')"
        >
          <el-select
            v-model="queryForm.supplyTableType"
            :placeholder="$t('CenterForSorting.pleaseChoose')"
            clearable
          >
            <el-option
              v-for="item in supplyTableTypeOptions"
              :key="item.value"
              :label="item.label"
              :value="item.value"
            />
          </el-select>
        </el-form-item>

        <el-form-item v-if="showCustomTime" :label="$t('CenterForSorting.timeRange')">
          <el-date-picker
            v-model="queryForm.customTime"
            :default-time="['00:00:00', '23:59:59']"
            :end-placeholder="$t('CenterForSorting.endTime')"
            :start-placeholder="$t('CenterForSorting.startTime')"
            type="datetimerange"
            unlink-panels
            value-format="yyyy-MM-dd HH:mm:ss"
          />
        </el-form-item>

        <el-form-item class="filter-actions">
          <el-button
            icon="el-icon-search"
            size="mini"
            type="primary"
            @click="handleSearch"
          >
            {{ $t('CenterForSorting.search') }}
          </el-button>
          <el-button icon="el-icon-refresh" size="mini" @click="resetQuery">
            {{ $t('CenterForSorting.reset') }}
          </el-button>
        </el-form-item>
      </el-form>
    </el-card>

    <div class="summary-grid">
      <div
        v-for="card in summaryCards"
        :key="card.key"
        class="summary-card"
        :class="`summary-card--${card.tone}`"
      >
        <div class="summary-label">
          <span>{{ card.label }}</span>
          <el-tooltip
            v-if="card.tooltip"
            :content="card.tooltip"
            effect="dark"
            placement="top"
          >
            <i class="el-icon-warning-outline summary-info-icon" />
          </el-tooltip>
        </div>
        <el-tooltip :content="String(card.value || '-')" effect="dark" placement="top">
          <div class="summary-value">{{ card.value }}</div>
        </el-tooltip>
        <div class="summary-extra">{{ card.extra }}</div>
      </div>
    </div>

    <div class="dashboard-grid">
      <el-card v-loading="pageLoading" shadow="never" class="page-card chart-card">
        <div class="card-header">
          <div>
            <div class="section-title">{{ overviewTitle }}</div>
            <div class="section-desc">
              {{ overviewRangeText }}
            </div>
          </div>
          <div class="header-tags">
            <span class="info-tag">{{ currentMetricLabel }}</span>
            <span class="info-tag">{{ currentQueryTypeLabel }}</span>
            <span class="info-tag">{{ realtimeStatusText }}</span>
            <el-button
              icon="el-icon-download"
              size="mini"
              class="download-btn"
              @click="downloadChartImage('overview')"
            >
              {{ $t('CenterForSorting.downloadImage') }}
            </el-button>
          </div>
        </div>
        <div ref="overviewChart" class="chart chart-large" />
      </el-card>

      <el-card v-loading="pageLoading" shadow="never" class="page-card chart-card ranking-card">
        <div class="card-header">
          <div>
            <div class="section-title">{{ rankingTitle }}</div>
            <div class="section-desc">{{ rankingTip }}</div>
          </div>
          <div class="header-tags">
            <span class="info-tag">
              {{ activeDimensionLabel }}: {{ formatNumber(dimensionItems.length) }}
            </span>
            <el-button
              icon="el-icon-download"
              size="mini"
              class="download-btn"
              @click="downloadChartImage('ranking')"
            >
              {{ $t('CenterForSorting.downloadImage') }}
            </el-button>
          </div>
        </div>
        <div ref="rankingChart" class="chart chart-ranking" />
      </el-card>
    </div>

    <div class="dashboard-grid detail-grid">
      <el-card v-loading="pageLoading" shadow="never" class="page-card table-card">
        <div class="card-header">
          <div>
            <div class="section-title">{{ summaryTitle }}</div>
            <div class="section-desc">{{ summaryTip }}</div>
          </div>
          <div class="header-tags">
            <span class="info-tag">{{ dimensionLabel }}</span>
          </div>
        </div>

        <el-table
          ref="dimensionTable"
          :data="dimensionItems"
          :empty-text="$t('CenterForSorting.noData')"
          :height="480"
          border
          highlight-current-row
          row-key="dimensionKey"
          @row-click="handleDimensionSelect"
        >
          <el-table-column
            :label="$t('CenterForSorting.ind')"
            align="center"
            type="index"
            width="60"
          />
          <el-table-column
            :label="dimensionNameLabel"
            min-width="140"
            prop="dimensionName"
            show-overflow-tooltip
          />
          <el-table-column
            :label="$t('CenterForSorting.sortingCurrentTotal')"
            align="right"
            min-width="120"
          >
            <template slot-scope="{ row }">
              <span>{{ formatNumber(row.compareData.currentTotal) }}</span>
            </template>
          </el-table-column>
          <el-table-column
            :label="$t('CenterForSorting.sortingPreviousTotal')"
            align="right"
            min-width="120"
          >
            <template slot-scope="{ row }">
              <span>{{ formatNumber(row.compareData.previousTotal) }}</span>
            </template>
          </el-table-column>
          <el-table-column
            :label="$t('CenterForSorting.sortingCompareRatio')"
            align="center"
            min-width="120"
          >
            <template slot-scope="{ row }">
              <el-tag
                size="mini"
                :type="getRatioTagType(row.compareData.ratio, row.compareData.comparable)"
              >
                {{ getCompareRatioText(row.compareData) }}
              </el-tag>
            </template>
          </el-table-column>
          <el-table-column
            :label="$t('CenterForSorting.sortingRealtime')"
            align="center"
            min-width="100"
          >
            <template slot-scope="{ row }">
              <el-tag size="mini" :type="row.realtime ? 'success' : 'info'">
                {{ row.realtime ? $t('CenterForSorting.yes') : $t('CenterForSorting.no') }}
              </el-tag>
            </template>
          </el-table-column>
        </el-table>
      </el-card>

      <el-card v-loading="pageLoading" shadow="never" class="page-card chart-card">
        <div class="card-header">
          <div>
            <div class="section-title">{{ detailTitle }}</div>
            <div class="section-desc">{{ detailTip }}</div>
          </div>
          <div class="header-tags">
            <span class="info-tag">{{ detailCurrentTotalText }}</span>
            <span class="info-tag">{{ detailRatioText }}</span>
            <el-button
              icon="el-icon-download"
              size="mini"
              class="download-btn"
              @click="downloadChartImage('detail')"
            >
              {{ $t('CenterForSorting.downloadImage') }}
            </el-button>
          </div>
        </div>
        <div ref="detailChart" class="chart chart-detail" />
      </el-card>
    </div>
  </div>
</template>

<script>
import * as echarts from 'echarts';
import dayjs from 'dayjs';
import { getAllMachineList } from '@/api/sorting/machine';
import { getAllCountChart, getDimensionCountChart } from '@/api/sorting/metric';

const METRIC_OPTIONS = {
  1: {
    labelKey: 'CenterForSorting.sortingChuteStat',
    descKey: 'CenterForSorting.sortingChuteStatDesc',
    actualSeriesKey: 'CenterForSorting.sortingActualBagCount',
    totalSeriesKey: 'CenterForSorting.sortingCumulativeBaggingCount',
    dimensionKey: 'chuteNo',
    dimensionNameLabelKey: 'CenterForSorting.sortingChuteName',
    peakLabelKey: 'CenterForSorting.sortingChutePeakValue',
    hotspotLabelKey: 'CenterForSorting.sortingTopChute',
    hotspotTipKey: 'CenterForSorting.sortingTopChuteTip',
    overviewTitleKey: 'CenterForSorting.sortingChuteOverviewTrend',
    rankingTitleKey: 'CenterForSorting.sortingChuteRanking',
    activeCountLabelKey: 'CenterForSorting.sortingChuteCountTotal',
    rankingTipKey: 'CenterForSorting.sortingChuteRankingTip',
    summaryTitleKey: 'CenterForSorting.sortingChuteSummary',
    summaryTipKey: 'CenterForSorting.sortingChuteDrillTip',
    detailChartTitleKey: 'CenterForSorting.sortingChuteDetailTrend',
    color: '#2563EB',
    lightColor: '#93C5FD'
  },
  2: {
    labelKey: 'CenterForSorting.sortingSupplyTableStat',
    descKey: 'CenterForSorting.sortingSupplyTableStatDesc',
    actualSeriesKey: 'CenterForSorting.sortingSupplyCount',
    totalSeriesKey: 'CenterForSorting.sortingCumulativeSupplyCount',
    dimensionKey: 'supplyTable',
    dimensionNameLabelKey: 'CenterForSorting.sortingSupplyTableName',
    peakLabelKey: 'CenterForSorting.sortingSupplyPeakValue',
    hotspotLabelKey: 'CenterForSorting.sortingTopSupplyTable',
    hotspotTipKey: 'CenterForSorting.sortingTopSupplyTableTip',
    overviewTitleKey: 'CenterForSorting.sortingSupplyOverviewTrend',
    rankingTitleKey: 'CenterForSorting.sortingSupplyRanking',
    activeCountLabelKey: 'CenterForSorting.sortingSupplyTableCountTotal',
    rankingTipKey: 'CenterForSorting.sortingSupplyRankingTip',
    summaryTitleKey: 'CenterForSorting.sortingSupplySummary',
    summaryTipKey: 'CenterForSorting.sortingSupplyDrillTip',
    detailChartTitleKey: 'CenterForSorting.sortingSupplyDetailTrend',
    color: '#D97706',
    lightColor: '#FCD34D'
  },
  3: {
    labelKey: 'CenterForSorting.sortingRuleHitStat',
    descKey: 'CenterForSorting.sortingRuleHitStatDesc',
    actualSeriesKey: 'CenterForSorting.sortingHitCount',
    totalSeriesKey: 'CenterForSorting.sortingCumulativeHit',
    dimensionKey: 'sortingRuleName',
    dimensionNameLabelKey: 'CenterForSorting.sortingRuleName',
    peakLabelKey: 'CenterForSorting.sortingRulePeakValue',
    hotspotLabelKey: 'CenterForSorting.sortingTopRule',
    hotspotTipKey: 'CenterForSorting.sortingTopRuleTip',
    overviewTitleKey: 'CenterForSorting.sortingRuleOverviewTrend',
    rankingTitleKey: 'CenterForSorting.sortingRuleRanking',
    activeCountLabelKey: 'CenterForSorting.sortingRuleCountTotal',
    rankingTipKey: 'CenterForSorting.sortingRuleRankingTip',
    summaryTitleKey: 'CenterForSorting.sortingRuleSummary',
    summaryTipKey: 'CenterForSorting.sortingRuleDrillTip',
    detailChartTitleKey: 'CenterForSorting.sortingRuleDetailTrend',
    color: '#7C3AED',
    lightColor: '#C4B5FD'
  }
};

const QUERY_TYPE_OPTIONS = [
  { value: 'HOUR', labelKey: 'CenterForSorting.sortingByHour' },
  { value: 'DAY', labelKey: 'CenterForSorting.sortingByDay' },
  { value: 'WEEK', labelKey: 'CenterForSorting.sortingByWeek' },
  { value: 'MONTH', labelKey: 'CenterForSorting.sortingByMonth' },
  { value: 'CUSTOM', labelKey: 'CenterForSorting.sortingCustomTime' }
];

const MACHINE_SUPPLY_TABLE_GROUPS = {
  'AJ-GRU': {
    manual: '1,2,3,4,5,6,7,13,14,15,16,17,18,19',
    automatic: '8,9,10,11,12,20,21,22,23,24'
  },
  'Perus01': {
    manual: '1,2,3,4,5,6',
    automatic: ''
  }
};

export default {
  name: 'SortingMetricReport',
  data() {
    return {
      machineLoading: false,
      loading: false,
      activeMetricType: '1',
      queryForm: {
        machineCode: '',
        queryType: 'DAY',
        customTime: this.getDefaultCustomTime(),
        supplyTableType: 'ALL'
      },
      machineList: [],
      allCountData: this.getEmptyAllCountData(),
      dimensionItems: [],
      selectedDimensionKey: '',
      overviewChart: null,
      rankingChart: null,
      detailChart: null,
      queryTypeOptions: QUERY_TYPE_OPTIONS,
      isFetchingDashboard: false,
      lastRefreshTime: '',
      hasLoadedOnce: false,
      isIntroMetaCompact: false
    };
  },
  computed: {
    pageLoading() {
      return !this.hasLoadedOnce && (this.machineLoading || this.loading);
    },
    metricTypeNumber() {
      return Number(this.activeMetricType || 1);
    },
    currentMetricConfig() {
      return METRIC_OPTIONS[this.metricTypeNumber] || METRIC_OPTIONS[1];
    },
    showCustomTime() {
      return this.queryForm.queryType === 'CUSTOM';
    },
    showSupplyTableFilter() {
      return this.metricTypeNumber === 2 && !!this.currentSupplyTableGroupConfig;
    },
    currentMetricLabel() {
      return this.$t(this.currentMetricConfig.labelKey);
    },
    currentMetricDesc() {
      return this.$t(this.currentMetricConfig.descKey);
    },
    overviewTitle() {
      return this.$t(this.currentMetricConfig.overviewTitleKey);
    },
    rankingTitle() {
      return this.$t(this.currentMetricConfig.rankingTitleKey);
    },
    activeDimensionLabel() {
      return this.$t(this.currentMetricConfig.activeCountLabelKey);
    },
    rankingTip() {
      return this.$t(this.currentMetricConfig.rankingTipKey);
    },
    summaryTitle() {
      return this.$t(this.currentMetricConfig.summaryTitleKey);
    },
    summaryTip() {
      return this.$t(this.currentMetricConfig.summaryTipKey);
    },
    detailChartTitle() {
      return this.$t(this.currentMetricConfig.detailChartTitleKey);
    },
    dimensionNameLabel() {
      return this.$t(this.currentMetricConfig.dimensionNameLabelKey);
    },
    currentQueryTypeLabel() {
      const target = this.queryTypeOptions.find((item) => item.value === this.queryForm.queryType);
      return target ? this.$t(target.labelKey) : '--';
    },
    currentMachineLabel() {
      const target = this.machineList.find((item) => item.machineCode === this.queryForm.machineCode);
      if (!target) {
        return this.$t('CenterForSorting.none');
      }
      return `${target.machineName}(${target.machineCode})`;
    },
    currentMachineInfo() {
      return this.machineList.find((item) => item.machineCode === this.queryForm.machineCode) || null;
    },
    currentSupplyTableGroupConfig() {
      const machineCode = this.queryForm.machineCode ? String(this.queryForm.machineCode).trim() : '';
      if (!machineCode || !MACHINE_SUPPLY_TABLE_GROUPS[machineCode]) {
        return null;
      }
      return MACHINE_SUPPLY_TABLE_GROUPS[machineCode];
    },
    dimensionLabel() {
      return this.$t(`CenterForSorting.${this.currentMetricConfig.dimensionKey}`);
    },
    supplyTableTypeOptions() {
      return [
        {
          value: 'ALL',
          label: this.$t('CenterForSorting.allSupplyTableType')
        },
        {
          value: 'MANUAL',
          label: this.$t('CenterForSorting.manualSupplyTable')
        }
      ].concat({
        value: 'AUTOMATIC',
        label: this.$t('CenterForSorting.automaticSupplyTable')
      });
    },
    selectedDimension() {
      return (
        this.dimensionItems.find((item) => String(item.dimensionKey) === String(this.selectedDimensionKey)) ||
        this.dimensionItems[0] ||
        null
      );
    },
    overviewRangeText() {
      const chartData = this.allCountData.chartData || {};
      if (!chartData.startTime || !chartData.endTime) {
        return this.$t('CenterForSorting.noData');
      }
      return `${chartData.startTime} ~ ${chartData.endTime}`;
    },
    reportTimeRangeText() {
      return this.overviewRangeText;
    },
    realtimeStatusText() {
      return this.allCountData.realtime
        ? this.$t('CenterForSorting.sortingRealtimeIncluded')
        : this.$t('CenterForSorting.sortingHistoryOnly');
    },
    hotspotTooltip() {
      if (!this.topDimensionItem) {
        return this.$t('CenterForSorting.noData');
      }
      return `${this.$t('CenterForSorting.sortingReportTimeRange')}: ${this.overviewRangeText}，${this.$t(this.currentMetricConfig.hotspotTipKey)}`;
    },
    peakPoint() {
      const points = (this.allCountData.chartData && this.allCountData.chartData.points) || [];
      if (!points.length) {
        return null;
      }
      return points.reduce((best, item) => {
        if (!best || Number(item.actualValue || 0) > Number(best.actualValue || 0)) {
          return item;
        }
        return best;
      }, null);
    },
    topDimensionItem() {
      return this.dimensionItems[0] || null;
    },
    summaryCards() {
      const compareData = this.allCountData.compareData || {};
      const peakPoint = this.peakPoint;
      const topDimension = this.topDimensionItem;
      const comparable = !!compareData.comparable;
      return [
        {
          key: 'currentTotal',
          tone: 'blue',
          label: this.$t('CenterForSorting.sortingCurrentTotal'),
          value: this.formatNumber(compareData.currentTotal),
          extra: this.realtimeStatusText
        },
        {
          key: 'previousTotal',
          tone: 'slate',
          label: this.$t('CenterForSorting.sortingPreviousTotal'),
          value: this.formatNumber(compareData.previousTotal),
          extra: comparable
            ? this.$t('CenterForSorting.sortingHasCompareData')
            : this.$t('CenterForSorting.sortingNoCompareData')
        },
        {
          key: 'ratio',
          tone: compareData.ratio > 0 ? 'green' : compareData.ratio < 0 ? 'amber' : 'purple',
          label: this.$t('CenterForSorting.sortingCompareRatio'),
          value: comparable ? this.formatRatio(compareData.ratio) : '--',
          extra: this.getTrendText(compareData.ratio, comparable)
        },
        {
          key: 'peak',
          tone: 'amber',
          label: this.$t(this.currentMetricConfig.peakLabelKey),
          value: peakPoint ? this.formatNumber(peakPoint.actualValue) : '--',
          extra: peakPoint ? peakPoint.label : this.$t('CenterForSorting.noData')
        },
        {
          key: 'topDimension',
          tone: 'purple',
          label: this.$t(this.currentMetricConfig.hotspotLabelKey),
          value: topDimension ? topDimension.dimensionName : '--',
          tooltip: this.hotspotTooltip,
          extra: topDimension
            ? `${this.dimensionLabel} ${topDimension.dimensionKey} / ${this.formatNumber(topDimension.compareData.currentTotal)}`
            : this.$t('CenterForSorting.noData')
        }
      ];
    },
    detailTitle() {
      return `${this.detailChartTitle}`;
    },
    detailTip() {
      if (!this.selectedDimension) {
        return this.$t('CenterForSorting.sortingSelectDimensionTip');
      }
      return `${this.dimensionLabel}: ${this.selectedDimension.dimensionName}`;
    },
    detailCurrentTotalText() {
      if (!this.selectedDimension) {
        return `${this.$t('CenterForSorting.sortingCurrentTotal')}: --`;
      }
      return `${this.$t('CenterForSorting.sortingCurrentTotal')}: ${this.formatNumber(this.selectedDimension.compareData.currentTotal)}`;
    },
    detailRatioText() {
      if (!this.selectedDimension) {
        return `${this.$t('CenterForSorting.sortingCompareRatio')}: --`;
      }
      return `${this.$t('CenterForSorting.sortingCompareRatio')}: ${this.getCompareRatioText(this.selectedDimension.compareData)}`;
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.initCharts();
      this.renderAllCharts();
    });
    this.fetchMachines();
    this.handleScroll();
    window.addEventListener('scroll', this.handleScroll, { passive: true });
    window.addEventListener('resize', this.handleResize);
  },
  beforeDestroy() {
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.handleResize);
    this.disposeCharts();
  },
  methods: {
    // 自定义时间默认取当天整天范围。
    getDefaultCustomTime() {
      return [
        dayjs().startOf('day').format('YYYY-MM-DD HH:mm:ss'),
        dayjs().endOf('day').format('YYYY-MM-DD HH:mm:ss')
      ];
    },
    getEmptyAllCountData() {
      return {
        machineCode: '',
        metricType: 1,
        queryType: 'DAY',
        chartData: {
          points: [],
          startTime: '',
          endTime: ''
        },
        compareData: {
          currentTotal: 0,
          previousTotal: 0,
          ratio: 0,
          comparable: false
        },
        realtime: false
      };
    },
    async fetchMachines() {
      this.machineLoading = true;
      try {
        const res = await getAllMachineList();
        this.machineList = res.data || [];
        if (!this.queryForm.machineCode && this.machineList.length) {
          this.queryForm.machineCode = this.machineList[0].machineCode;
        }
        await this.fetchDashboard();
      } catch (error) {
        this.machineList = [];
      } finally {
        this.machineLoading = false;
      }
    },
    handleScroll() {
      this.isIntroMetaCompact = window.scrollY > 80;
    },
    handleManualRefresh() {
      this.fetchDashboard({ forceSilent: true });
    },
    handleMetricTabClick() {
      this.queryForm.supplyTableType = 'ALL';
      this.selectedDimensionKey = '';
      this.fetchDashboard();
    },
    handleMachineChange() {
      this.selectedDimensionKey = '';
      this.fetchDashboard();
    },
    handleQueryTypeChange(value) {
      if (value !== 'CUSTOM') {
        this.selectedDimensionKey = '';
        this.fetchDashboard();
      }
    },
    handleSearch() {
      this.selectedDimensionKey = '';
      this.fetchDashboard();
    },
    resetQuery() {
      this.activeMetricType = '1';
      this.queryForm = {
        machineCode: this.machineList.length ? this.machineList[0].machineCode : '',
        queryType: 'DAY',
        customTime: this.getDefaultCustomTime(),
        supplyTableType: 'ALL'
      };
      this.selectedDimensionKey = '';
      this.fetchDashboard();
    },
    validateQuery() {
      if (!this.queryForm.machineCode) {
        this.$message.warning(this.$t('CenterForSorting.selectMachineFirst'));
        return false;
      }
      if (this.showCustomTime && (!this.queryForm.customTime || this.queryForm.customTime.length !== 2)) {
        this.$message.warning(this.$t('CenterForSorting.timeRangeRequired'));
        return false;
      }
      return true;
    },
    buildRequestParams() {
      const params = {
        machineCode: this.queryForm.machineCode,
        metricType: this.metricTypeNumber,
        queryType: this.queryForm.queryType
      };
      const dimensionKeys = this.getActiveDimensionKeys();
      if (dimensionKeys.length) {
        params.dimensionKeys = dimensionKeys;
      }
      if (this.showCustomTime) {
        params.startTime = this.queryForm.customTime[0];
        params.endTime = this.queryForm.customTime[1];
      }
      return params;
    },
    getActiveDimensionKeys() {
      if (this.metricTypeNumber === 2) {
        const selectedType = this.queryForm.supplyTableType || 'ALL';
        if (selectedType === 'ALL') {
          return [];
        }
        if (!this.currentSupplyTableGroupConfig) {
          return [];
        }
        if (selectedType === 'MANUAL') {
          return this.parseSupplyTableGroupValues(this.currentSupplyTableGroupConfig.manual);
        }
        if (selectedType === 'AUTOMATIC') {
          return this.parseSupplyTableGroupValues(this.currentSupplyTableGroupConfig.automatic);
        }
      }
      return [];
    },
    parseSupplyTableGroupValues(value) {
      if (Array.isArray(value)) {
        return value.map((item) => String(item).trim()).filter(Boolean);
      }
      return String(value || '')
        .split(',')
        .map((item) => item.trim())
        .filter(Boolean);
    },
    normalizeSupplyTableEnumValue(value) {
      const rawValue = String(value || '').trim();
      if (!rawValue) {
        return '';
      }
      const matched = rawValue.match(/(\d{1,2})$/);
      if (!matched) {
        return rawValue;
      }
      const normalizedNumber = Number(matched[1]);
      if (!Number.isInteger(normalizedNumber) || normalizedNumber < 1 || normalizedNumber > 24) {
        return rawValue;
      }
      return String(normalizedNumber);
    },
    getSupplyTableTranslationKey(value) {
      const machineCode = this.queryForm.machineCode ? String(this.queryForm.machineCode).trim() : '';
      const normalizedValue = this.normalizeSupplyTableEnumValue(value);
      if (!machineCode || !normalizedValue) {
        return '';
      }
      return `${machineCode}${normalizedValue}`;
    },
    getSupplyTableDisplayLabel(value) {
      const normalizedValue = this.normalizeSupplyTableEnumValue(value);
      if (!normalizedValue) {
        return '--';
      }
      const translationKey = this.getSupplyTableTranslationKey(normalizedValue);
      if (translationKey && this.$te(`CenterForSorting.${translationKey}`)) {
        return this.$t(`CenterForSorting.${translationKey}`);
      }
      return normalizedValue;
    },
    getSupplyTableOptionLabel(value) {
      return this.getSupplyTableDisplayLabel(value);
    },
    normalizeAllCountData(data) {
      const emptyData = this.getEmptyAllCountData();
      const chartData = data && data.chartData ? data.chartData : {};
      const compareData = data && data.compareData ? data.compareData : {};
      return {
        machineCode: (data && data.machineCode) || emptyData.machineCode,
        metricType: (data && data.metricType) || this.metricTypeNumber,
        queryType: (data && data.queryType) || this.queryForm.queryType,
        chartData: {
          points: Array.isArray(chartData.points)
            ? chartData.points.map((item) => ({
              label: item.label || '',
              actualValue: Number(item.actualValue || 0),
              totalValue: Number(item.totalValue || 0)
            }))
            : [],
          startTime: chartData.startTime || '',
          endTime: chartData.endTime || ''
        },
        compareData: {
          currentTotal: Number(compareData.currentTotal || 0),
          previousTotal: Number(compareData.previousTotal || 0),
          ratio: Number(compareData.ratio || 0),
          comparable: !!compareData.comparable
        },
        realtime: !!(data && data.realtime)
      };
    },
    normalizeDimensionItems(items) {
      if (!Array.isArray(items)) {
        return [];
      }
      return items.map((item) => {
        const chartData = item.chartData || {};
        const compareData = item.compareData || {};
        const dimensionKey = item.dimensionKey || '--';
        return {
          dimensionKey,
          dimensionName: this.getDimensionDisplayName(item.dimensionName, dimensionKey),
          chartData: {
            points: Array.isArray(chartData.points)
              ? chartData.points.map((point) => ({
                label: point.label || '',
                actualValue: Number(point.actualValue || 0),
                totalValue: Number(point.totalValue || 0)
              }))
              : [],
            startTime: chartData.startTime || '',
            endTime: chartData.endTime || ''
          },
          compareData: {
            currentTotal: Number(compareData.currentTotal || 0),
            previousTotal: Number(compareData.previousTotal || 0),
            ratio: Number(compareData.ratio || 0),
            comparable: !!compareData.comparable
          },
          realtime: !!item.realtime
        };
      });
    },
    getDimensionDisplayName(dimensionName, dimensionKey) {
      if (this.metricTypeNumber === 2) {
        return this.getSupplyTableDisplayLabel(dimensionKey);
      }
      return dimensionName || dimensionKey || '--';
    },
    clearDashboard() {
      this.allCountData = this.getEmptyAllCountData();
      this.dimensionItems = [];
      this.selectedDimensionKey = '';
      this.renderAllCharts();
    },
    async fetchDashboard(options = {}) {
      if (!this.validateQuery()) {
        return;
      }
      if (this.isFetchingDashboard) {
        return;
      }
      const { silent = false, forceSilent = false } = options;
      const shouldSilent = forceSilent || silent || this.hasLoadedOnce;
      this.isFetchingDashboard = true;
      if (!shouldSilent && !this.hasLoadedOnce) {
        this.loading = true;
      }
      try {
        const params = this.buildRequestParams();
        const [allCountRes, dimensionRes] = await Promise.all([
          getAllCountChart(params),
          getDimensionCountChart(params)
        ]);
        this.allCountData = this.normalizeAllCountData(allCountRes.data || {});
        this.dimensionItems = this.normalizeDimensionItems((dimensionRes.data && dimensionRes.data.items) || []);
        if (!this.dimensionItems.some((item) => String(item.dimensionKey) === String(this.selectedDimensionKey))) {
          this.selectedDimensionKey = this.dimensionItems.length ? this.dimensionItems[0].dimensionKey : '';
        }
        this.lastRefreshTime = dayjs().format('YYYY-MM-DD HH:mm:ss');
        this.$nextTick(() => {
          this.syncCurrentRow();
          this.renderAllCharts();
        });
      } catch (error) {
        if (!shouldSilent) {
          this.clearDashboard();
        }
      } finally {
        this.isFetchingDashboard = false;
        if (!this.hasLoadedOnce) {
          this.hasLoadedOnce = true;
        }
        this.loading = false;
      }
    },
    getChartTitleByType(type) {
      const map = {
        overview: this.overviewTitle,
        ranking: this.rankingTitle,
        detail: this.detailTitle || this.detailChartTitle
      };
      return map[type] || '';
    },
    getChartTagByType(type) {
      const map = {
        overview: this.$t('CenterForSorting.sortingOverviewTrend'),
        ranking: this.$t('CenterForSorting.sortingDimensionRanking'),
        detail: this.$t('CenterForSorting.sortingDetailTrend')
      };
      return map[type] || '';
    },
    getDownloadTitle(type) {
      const title = this.getChartTitleByType(type);
      if (title) {
        return title;
      }
      return this.getChartTagByType(type);
    },
    handleDimensionSelect(row) {
      if (!row) {
        return;
      }
      this.selectedDimensionKey = row.dimensionKey;
      this.$nextTick(() => {
        this.syncCurrentRow();
        this.renderRankingChart();
        this.renderDetailChart();
      });
    },
    syncCurrentRow() {
      const tableRef = this.$refs.dimensionTable;
      if (!tableRef || !this.selectedDimension) {
        return;
      }
      tableRef.setCurrentRow(this.selectedDimension);
    },
    initCharts() {
      if (!this.overviewChart && this.$refs.overviewChart) {
        this.overviewChart = echarts.init(this.$refs.overviewChart);
      }
      if (!this.rankingChart && this.$refs.rankingChart) {
        this.rankingChart = echarts.init(this.$refs.rankingChart);
        this.rankingChart.on('click', (params) => {
          const item = this.dimensionItems[params.dataIndex];
          if (item) {
            this.handleDimensionSelect(item);
          }
        });
      }
      if (!this.detailChart && this.$refs.detailChart) {
        this.detailChart = echarts.init(this.$refs.detailChart);
      }
    },
    disposeCharts() {
      [this.overviewChart, this.rankingChart, this.detailChart].forEach((chart) => {
        if (chart) {
          chart.dispose();
        }
      });
      this.overviewChart = null;
      this.rankingChart = null;
      this.detailChart = null;
    },
    handleResize() {
      [this.overviewChart, this.rankingChart, this.detailChart].forEach((chart) => {
        if (chart) {
          chart.resize();
        }
      });
    },
    renderAllCharts() {
      this.initCharts();
      this.renderOverviewChart();
      this.renderRankingChart();
      this.renderDetailChart();
    },
    getChartMeta(type) {
      const map = {
        overview: {
          chart: this.overviewChart,
          title: this.getDownloadTitle('overview')
        },
        ranking: {
          chart: this.rankingChart,
          title: this.getDownloadTitle('ranking')
        },
        detail: {
          chart: this.detailChart,
          title: this.getDownloadTitle('detail')
        }
      };
      return map[type] || null;
    },
    getChartFileName(title) {
      const machineCode = this.queryForm.machineCode || 'machine';
      const queryType = this.queryForm.queryType || 'query';
      const metricName = this.currentMetricLabel || 'metric';
      return `${metricName}_${title}_${machineCode}_${queryType}_${dayjs().format('YYYYMMDDHHmmss')}.png`;
    },
    triggerDownload(url, fileName) {
      const link = document.createElement('a');
      link.href = url;
      link.download = fileName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    },
    downloadChartImage(type) {
      const chartMeta = this.getChartMeta(type);
      if (!chartMeta || !chartMeta.chart) {
        this.$message.warning(this.$t('CenterForSorting.downloadImageUnavailable'));
        return;
      }
      try {
        const url = chartMeta.chart.getDataURL({
          type: 'png',
          pixelRatio: 2,
          backgroundColor: '#ffffff'
        });
        this.triggerDownload(url, this.getChartFileName(chartMeta.title));
        this.$message.success(this.$t('CenterForSorting.downloadImageSuccess'));
      } catch (error) {
        this.$message.error(this.$t('CenterForSorting.downloadImageFailed'));
      }
    },
    getEmptyChartOption(text) {
      return {
        title: {
          text,
          left: 'center',
          top: 'center',
          textStyle: {
            color: '#94A3B8',
            fontSize: 14,
            fontWeight: 'normal'
          }
        },
        xAxis: {
          show: false
        },
        yAxis: {
          show: false
        },
        series: []
      };
    },
    renderOverviewChart() {
      if (!this.overviewChart) {
        return;
      }
      const points = this.allCountData.chartData.points || [];
      if (!points.length) {
        this.overviewChart.setOption(this.getEmptyChartOption(this.$t('CenterForSorting.noData')), true);
        return;
      }
      const { color, lightColor, actualSeriesKey, totalSeriesKey } = this.currentMetricConfig;
      this.overviewChart.setOption(
        {
          color: [color, lightColor],
          tooltip: {
            trigger: 'axis',
            backgroundColor: 'rgba(15, 23, 42, 0.92)',
            borderColor: 'rgba(148, 163, 184, 0.2)',
            textStyle: {
              color: '#E2E8F0'
            }
          },
          legend: {
            top: 0,
            textStyle: {
              color: '#475569'
            }
          },
          grid: {
            left: 16,
            right: 24,
            top: 54,
            bottom: 18,
            containLabel: true
          },
          xAxis: {
            type: 'category',
            boundaryGap: false,
            data: points.map((item) => item.label),
            axisLine: {
              lineStyle: {
                color: '#CBD5E1'
              }
            },
            axisLabel: {
              color: '#64748B'
            }
          },
          yAxis: {
            type: 'value',
            splitLine: {
              lineStyle: {
                color: 'rgba(148, 163, 184, 0.18)'
              }
            },
            axisLabel: {
              color: '#64748B'
            }
          },
          series: [
            {
              name: this.$t(actualSeriesKey),
              type: 'line',
              smooth: true,
              symbol: 'circle',
              symbolSize: 7,
              lineStyle: {
                width: 3
              },
              areaStyle: {
                color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                  { offset: 0, color: `${color}55` },
                  { offset: 1, color: `${color}05` }
                ])
              },
              data: points.map((item) => item.actualValue)
            },
            {
              name: this.$t(totalSeriesKey),
              type: 'line',
              smooth: true,
              symbol: 'circle',
              symbolSize: 6,
              lineStyle: {
                width: 3,
                type: 'dashed'
              },
              data: points.map((item) => item.totalValue)
            }
          ]
        },
        true
      );
    },
    renderRankingChart() {
      if (!this.rankingChart) {
        return;
      }
      if (!this.dimensionItems.length) {
        this.rankingChart.setOption(this.getEmptyChartOption(this.$t('CenterForSorting.noData')), true);
        return;
      }
      const selectedKey = String(this.selectedDimensionKey);
      const endValue = Math.min(this.dimensionItems.length - 1, 11);
      this.rankingChart.setOption(
        {
          tooltip: {
            trigger: 'axis',
            axisPointer: {
              type: 'shadow'
            },
            backgroundColor: 'rgba(15, 23, 42, 0.92)',
            borderColor: 'rgba(148, 163, 184, 0.2)',
            textStyle: {
              color: '#E2E8F0'
            },
            formatter: (params) => {
              const item = this.dimensionItems[params[0].dataIndex];
              if (!item) {
                return '';
              }
              return [
                `${item.dimensionName}`,
                `${this.dimensionLabel}: ${item.dimensionKey}`,
                `${this.$t('CenterForSorting.sortingCurrentTotal')}: ${this.formatNumber(item.compareData.currentTotal)}`,
                `${this.$t('CenterForSorting.sortingCompareRatio')}: ${this.getCompareRatioText(item.compareData)}`
              ].join('<br/>');
            }
          },
          grid: {
            left: 12,
            right: this.dimensionItems.length > 12 ? 38 : 18,
            top: 18,
            bottom: 18,
            containLabel: true
          },
          xAxis: {
            type: 'value',
            splitLine: {
              lineStyle: {
                color: 'rgba(148, 163, 184, 0.18)'
              }
            },
            axisLabel: {
              color: '#64748B'
            }
          },
          yAxis: {
            type: 'category',
            inverse: true,
            data: this.dimensionItems.map((item) => item.dimensionName),
            axisTick: {
              show: false
            },
            axisLine: {
              show: false
            },
            axisLabel: {
              color: '#475569',
              width: 110,
              overflow: 'truncate'
            }
          },
          dataZoom: this.dimensionItems.length > 12
            ? [
              {
                type: 'inside',
                yAxisIndex: 0,
                startValue: 0,
                endValue
              },
              {
                type: 'slider',
                yAxisIndex: 0,
                width: 12,
                right: 0,
                top: 24,
                bottom: 24,
                startValue: 0,
                endValue
              }
            ]
            : [],
          series: [
            {
              type: 'bar',
              barWidth: 14,
              data: this.dimensionItems.map((item, index) => {
                let barColor = '#60A5FA';
                if (String(item.dimensionKey) === selectedKey) {
                  barColor = '#1D4ED8';
                } else if (index === 0) {
                  barColor = '#F59E0B';
                } else if (index === 1) {
                  barColor = '#3B82F6';
                } else if (index === 2) {
                  barColor = '#8B5CF6';
                }
                return {
                  value: item.compareData.currentTotal,
                  itemStyle: {
                    color: barColor,
                    borderRadius: [0, 8, 8, 0]
                  }
                };
              }),
              label: {
                show: true,
                position: 'right',
                color: '#334155',
                formatter: ({ value }) => this.formatNumber(value)
              }
            }
          ]
        },
        true
      );
    },
    renderDetailChart() {
      if (!this.detailChart) {
        return;
      }
      const target = this.selectedDimension;
      if (!target || !target.chartData.points.length) {
        this.detailChart.setOption(this.getEmptyChartOption(this.$t('CenterForSorting.sortingSelectDimensionTip')), true);
        return;
      }
      const { color, lightColor, actualSeriesKey, totalSeriesKey } = this.currentMetricConfig;
      this.detailChart.setOption(
        {
          color: [lightColor, color],
          tooltip: {
            trigger: 'axis',
            backgroundColor: 'rgba(15, 23, 42, 0.92)',
            borderColor: 'rgba(148, 163, 184, 0.2)',
            textStyle: {
              color: '#E2E8F0'
            }
          },
          legend: {
            top: 0,
            textStyle: {
              color: '#475569'
            }
          },
          grid: {
            left: 16,
            right: 24,
            top: 54,
            bottom: 18,
            containLabel: true
          },
          xAxis: {
            type: 'category',
            boundaryGap: false,
            data: target.chartData.points.map((item) => item.label),
            axisLine: {
              lineStyle: {
                color: '#CBD5E1'
              }
            },
            axisLabel: {
              color: '#64748B'
            }
          },
          yAxis: {
            type: 'value',
            splitLine: {
              lineStyle: {
                color: 'rgba(148, 163, 184, 0.18)'
              }
            },
            axisLabel: {
              color: '#64748B'
            }
          },
          series: [
            {
              name: this.$t(actualSeriesKey),
              type: 'line',
              smooth: true,
              symbol: 'circle',
              symbolSize: 6,
              lineStyle: {
                width: 3
              },
              areaStyle: {
                color: new echarts.graphic.LinearGradient(0, 0, 0, 1, [
                  { offset: 0, color: `${lightColor}55` },
                  { offset: 1, color: `${lightColor}06` }
                ])
              },
              data: target.chartData.points.map((item) => item.actualValue)
            },
            {
              name: this.$t(totalSeriesKey),
              type: 'line',
              smooth: true,
              symbol: 'circle',
              symbolSize: 6,
              lineStyle: {
                width: 3
              },
              data: target.chartData.points.map((item) => item.totalValue)
            }
          ]
        },
        true
      );
    },
    formatNumber(value) {
      return Number(value || 0).toLocaleString('en-US');
    },
    formatRatio(value) {
      const number = Number(value || 0);
      const sign = number > 0 ? '+' : '';
      return `${sign}${number.toFixed(2)}%`;
    },
    getTrendText(ratio, comparable) {
      if (!comparable) {
        return this.$t('CenterForSorting.sortingNoCompareData');
      }
      if (ratio > 0) {
        return this.$t('CenterForSorting.sortingTrendUp');
      }
      if (ratio < 0) {
        return this.$t('CenterForSorting.sortingTrendDown');
      }
      return this.$t('CenterForSorting.sortingTrendFlat');
    },
    getCompareRatioText(compareData) {
      if (!compareData || !compareData.comparable) {
        return '--';
      }
      return this.formatRatio(compareData.ratio);
    },
    getRatioTagType(ratio, comparable) {
      if (!comparable) {
        return 'info';
      }
      if (ratio > 0) {
        return 'success';
      }
      if (ratio < 0) {
        return 'warning';
      }
      return 'info';
    }
  }
};
</script>

<style scoped>
.sorting-metric-report {
  background: #f8fafc;
}

.page-card {
  border: 1px solid #e2e8f0;
  border-radius: 16px;
}

.page-intro {
  margin-bottom: 12px;
  background: linear-gradient(135deg, #eff6ff 0%, #ffffff 58%, #fff7ed 100%);
}

.intro-main {
  display: flex;
  align-items: center;
  justify-content: flex-start;
  gap: 16px;
  flex-wrap: wrap;
}

.intro-heading {
  min-width: 0;
  flex: 1;
}

.intro-title-row {
  display: flex;
  align-items: center;
  gap: 14px;
  flex-wrap: wrap;
}

.page-title {
  font-size: 24px;
  line-height: 32px;
  font-weight: 700;
  color: #0f172a;
  white-space: nowrap;
}

.page-desc {
  max-width: 760px;
  font-size: 13px;
  line-height: 20px;
  color: #64748b;
}

.intro-meta {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  position: sticky;
  top: 8px;
  z-index: 20;
  margin-bottom: 14px;
  padding: 8px 10px;
  border: 1px solid rgba(203, 213, 225, 0.9);
  border-radius: 16px;
  background: rgba(255, 255, 255, 0.92);
  box-shadow: 0 10px 30px rgba(15, 23, 42, 0.08);
  backdrop-filter: blur(12px);
  transition: padding 0.2s ease, gap 0.2s ease, top 0.2s ease, box-shadow 0.2s ease;
}

.intro-meta--compact {
  top: 4px;
  gap: 6px;
  padding: 5px 8px;
  box-shadow: 0 6px 18px rgba(15, 23, 42, 0.08);
}

.meta-item {
  min-width: 148px;
  flex: 1 1 180px;
  padding: 8px 10px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  background: rgba(248, 250, 252, 0.92);
  transition: padding 0.2s ease, min-width 0.2s ease, border-radius 0.2s ease;
}

.meta-item--range {
  min-width: 280px;
  flex: 2 1 420px;
}

.meta-label {
  font-size: 11px;
  line-height: 14px;
  color: #64748b;
  white-space: nowrap;
}

.meta-value {
  margin-top: 3px;
  font-size: 12px;
  line-height: 16px;
  font-weight: 600;
  color: #1e3a8a;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: default;
}

.meta-value--small {
  font-size: 11px;
  line-height: 15px;
}

.meta-range-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;
  flex-wrap: nowrap;
  min-width: 0;
}

.meta-range-row .el-tooltip {
  flex: 1 1 240px;
  min-width: 0;
}

.meta-refresh-btn {
  border: none;
  border-radius: 999px;
  padding: 6px 12px;
  color: #ffffff;
  background: linear-gradient(135deg, #2563eb 0%, #1d4ed8 100%);
  box-shadow: 0 10px 22px rgba(37, 99, 235, 0.24);
  transition: transform 0.2s ease, box-shadow 0.2s ease, opacity 0.2s ease;
  flex-shrink: 0;
}

.intro-meta--compact .meta-item {
  min-width: 120px;
  padding: 5px 8px;
  border-radius: 10px;
}

.intro-meta--compact .meta-item--range {
  min-width: 220px;
}

.intro-meta--compact .meta-label {
  font-size: 10px;
  line-height: 12px;
}

.intro-meta--compact .meta-value {
  margin-top: 2px;
  font-size: 11px;
  line-height: 14px;
}

.intro-meta--compact .meta-value--small {
  font-size: 10px;
  line-height: 13px;
}

.intro-meta--compact .meta-refresh-btn {
  padding: 4px 10px;
}

.meta-refresh-btn:hover,
.meta-refresh-btn:focus {
  color: #ffffff;
  transform: translateY(-1px);
  box-shadow: 0 14px 24px rgba(37, 99, 235, 0.3);
}

.meta-refresh-btn.is-loading {
  opacity: 0.9;
}

.filter-card {
  margin-bottom: 16px;
}

.metric-tab-bar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 16px;
}

.section-title {
  font-size: 16px;
  line-height: 24px;
  font-weight: 700;
  color: #0f172a;
}

.section-desc {
  margin-top: 6px;
  font-size: 13px;
  line-height: 20px;
  color: #64748b;
}

.metric-tabs {
  flex: 1;
}

.metric-desc-trigger {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 8px 12px;
  border: 1px solid #dbeafe;
  border-radius: 999px;
  background: linear-gradient(135deg, #eff6ff 0%, #f8fafc 100%);
  color: #1d4ed8;
  font-size: 12px;
  line-height: 16px;
  font-weight: 600;
  white-space: nowrap;
  cursor: pointer;
  transition: all 0.2s ease;
}

.metric-desc-trigger:hover {
  border-color: #93c5fd;
  box-shadow: 0 8px 20px rgba(37, 99, 235, 0.12);
}

.filter-form {
  margin-top: 12px;
  display: flex;
  flex-wrap: wrap;
  align-items: flex-end;
}

.filter-actions {
  margin-left: auto;
}

.summary-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 16px;
  margin-bottom: 16px;
}

.summary-card {
  position: relative;
  padding: 18px 20px;
  border-radius: 16px;
  background: #ffffff;
  border: 1px solid #e2e8f0;
  overflow: hidden;
}

.summary-card::after {
  content: '';
  position: absolute;
  right: -28px;
  bottom: -32px;
  width: 112px;
  height: 112px;
  border-radius: 50%;
  opacity: 0.12;
}

.summary-card--blue::after {
  background: #2563eb;
}

.summary-card--slate::after {
  background: #64748b;
}

.summary-card--green::after {
  background: #16a34a;
}

.summary-card--amber::after {
  background: #d97706;
}

.summary-card--purple::after {
  background: #7c3aed;
}

.summary-label {
  display: flex;
  align-items: center;
  gap: 6px;
  font-size: 12px;
  color: #64748b;
}

.summary-info-icon {
  color: #94a3b8;
  cursor: pointer;
  transition: color 0.2s ease;
}

.summary-info-icon:hover {
  color: #2563eb;
}

.summary-value {
  margin-top: 12px;
  display: block;
  width: 100%;
  font-size: 28px;
  line-height: 34px;
  font-weight: 700;
  color: #0f172a;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.summary-extra {
  margin-top: 10px;
  font-size: 12px;
  line-height: 18px;
  color: #64748b;
}

.dashboard-grid {
  display: grid;
  grid-template-columns: minmax(0, 1.45fr) minmax(360px, 0.95fr);
  gap: 16px;
  margin-bottom: 16px;
}

.detail-grid {
  grid-template-columns: minmax(0, 1.1fr) minmax(380px, 0.9fr);
}

.chart-card,
.table-card {
  min-width: 0;
}

.ranking-card {
  overflow: hidden;
}

.card-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 14px;
}

.header-tags {
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  justify-content: flex-end;
}

.info-tag {
  display: inline-flex;
  align-items: center;
  padding: 6px 10px;
  border-radius: 999px;
  background: #eff6ff;
  color: #1d4ed8;
  font-size: 12px;
  line-height: 16px;
  font-weight: 600;
  white-space: nowrap;
}

.download-btn {
  border-radius: 999px;
}

.filter-card ::v-deep .el-tabs__header {
  margin: 0;
}

.filter-card ::v-deep .el-tabs__nav-wrap::after {
  display: none;
}

.filter-card ::v-deep .el-tabs__nav {
  display: flex;
  gap: 10px;
  padding: 6px;
  border-radius: 999px;
  background: #f1f5f9;
}

.filter-card ::v-deep .el-tabs__item {
  height: 38px;
  line-height: 38px;
  padding: 0 20px;
  border-radius: 999px;
  color: #475569;
  font-size: 13px;
  font-weight: 600;
  transition: all 0.2s ease;
}

.filter-card ::v-deep .el-tabs__item.is-active {
  color: #0f172a;
  background: linear-gradient(135deg, #ffffff 0%, #dbeafe 100%);
  box-shadow: 0 10px 24px rgba(37, 99, 235, 0.16);
}

.filter-card ::v-deep .el-tabs__active-bar {
  display: none;
}

.chart {
  width: 100%;
}

.chart-large {
  height: 420px;
}

.chart-ranking,
.chart-detail {
  height: 480px;
}

.table-card ::v-deep .el-table__body tr.current-row > td {
  background: #eff6ff !important;
}

.table-card ::v-deep .el-table th {
  color: #475569;
  background: #f8fafc;
}

.table-card ::v-deep .el-table td {
  color: #0f172a;
}

@media (max-width: 1280px) {
  .dashboard-grid,
  .detail-grid {
    grid-template-columns: 1fr;
  }
}

@media (max-width: 768px) {
  .intro-main,
  .card-header {
    flex-direction: column;
  }

  .metric-tab-bar {
    flex-direction: column;
    align-items: stretch;
  }

  .filter-actions {
    margin-left: 0;
  }

  .intro-title-row {
    align-items: flex-start;
  }

  .intro-meta {
    top: 4px;
  }

  .chart-large,
  .chart-ranking,
  .chart-detail {
    height: 360px;
  }
}
</style>
